import { BusModuleRoute } from 'frontend/bus-module-routes'
import { BusModuleSubsystem } from 'frontend/bus-module-subsystems'
import { ClientGroup } from 'frontend/client-groups'
import { IDataModelTypeConfig } from 'frontend/dataModels/types/IDataModelTypeConfig'
import { ManualDriverSource } from 'frontend/driver-sources'
import { Osl } from 'frontend/osls'
import { VehicleSource } from 'frontend/vehicle-sources'
import { VehicleType } from 'frontend/vehicle-types'
import { BusTimetableImportStatus, BusTimetableType } from 'types/models/bus-timetable'
import { markRaw } from 'vue'

export const BusTimetableDataModelTypeConfig: IDataModelTypeConfig = {
  fields: [
    'id',
    'eid',
    'name',
    'updated_at',
    'created_at',
    'type',
    'dates_of_service',
    'min_pax_number',
    'import_status',
    'use_manual_driver_source_values',
    'manual_default_shift_length',
    'manual_max_break_time',
    'manual_min_break_time',
    'manual_min_time_between_shifts',
    'manual_max_cont_driving_time',
    { bus_route: ['id', 'name', 'color'] },
    { bus_module_subsystem: ['id', 'name'] },
    { driver_source: ['id', 'name'] },
    { vehicle_type: ['id', 'name'] },
    { vehicle_source: ['id', 'name', 'billable', 'maintainable'] },
    { client_group: ['id', 'name', 'background_color', 'border_color', 'font_color'] },
    {
      operational_service_level: [
        'id',
        'name',
        'font_color',
        'background_color',
        'border_color',
        'description',
      ],
    },
    {
      bus_timetable_frequency_intervals: [
        'id',
        'start_at',
        'end_at',
        'minutes_every',
        'is_empty_runs_interval',
        {
          bus_timetable_trips: [
            'id',
            'start_at',
            'bus_timetable_frequency_interval_id',
            'trip_data',
            'position',
          ],
        },
      ],
    },
    {
      bus_timetable_demand_intervals: [
        'id',
        'start_at',
        'end_at',
        'pax',
        'is_empty_runs_interval',
        {
          bus_timetable_trips: [
            'id',
            'start_at',
            'bus_timetable_demand_interval_id',
            'trip_data',
            'position',
          ],
        },
      ],
    },
  ],
  subscriptions: {
    items: 'BusTimetablesChannel',
    busRoute: 'BusRoutesFlatChannel',
    busModuleSubsystems: 'BusModuleSubsystemsFlatChannel',
    driverSource: 'DriverSourcesSimpleChannel',
    vehicleTypes: 'VehicleTypesSimpleChannel',
    vehicleSource: 'VehicleSourcesSimpleChannel',
    operationalServiceLevel: 'OperationalServiceLevelsSimpleChannel',
    clientGroup: 'ClientGroupsSimpleChannel',
  },
  defaultColumns: [
    'eid',
    'name',
    'datesOfService',
    'type',
    'busRoute',
    'busModuleSubsystem',
    'importStatus',
    'minPaxNumber',
    'driverSource',
    'vehicleType',
    'vehicleSource',
    'operationalServiceLevel',
    'clientGroup',
    'bus_timetable_frequency_intervals',
    'bus_timetable_demand_intervals',
  ],
  columnConfig: {
    eid: {
      label: 'EID',
      header: {
        sort: 'eid',
      },
      cell: {
        kind: 'simple',
        path: 'eid',
      },
    },
    name: {
      label: 'Name',
      header: {
        sort: 'name',
      },
      cell: {
        kind: 'simple',
        path: 'name',
      },
    },
    datesOfService: {
      label: 'Dates Of Service',
      cell: {
        kind: 'component',
        component: 'limited-components-list',
        componentProps: el => ({
          items: el.datesOfService,
          limit: 3,
          component: 'formatted-time',
          componentProps: time => ({
            t: time,
            tz: 'UTC',
            noInBrowserTimezone: true,
            noTooltips: true,
            format: 'dateOnly',
          }),
        }),
      },
    },
    type: {
      label: 'Type',
      header: {
        sort: 'type',
      },
      cell: {
        kind: 'function',
        fn: item => {
          switch (item.type as BusTimetableType) {
            case 'FrequencyBusTimetable':
              return 'Frequency-based shuttles'
            case 'DemandBusTimetable':
              return 'Demand-based shuttles'
            default:
              break
          }
        },
        path: 'type',
      },
    },
    importStatus: {
      label: 'Shuttle creation status',
      header: {
        sort: 'importStatus',
      },
      cell: {
        kind: 'function',
        fn: item => {
          switch (item.importStatus as BusTimetableImportStatus) {
            case 'new':
              return 'New'
            case 'generating':
              return 'In progress'
            case 'finished':
              return 'Finished'
            case 'error':
              return 'Error'
            default:
              break
          }
        },
        path: 'importStatus',
      },
    },
    busRoute: {
      label: 'Route',
      cell: {
        kind: 'component',
        component: markRaw(BusModuleRoute),
        componentProps: data => ({ busModuleRoute: data.busRoute }),
      },
    },
    busModuleSubsystem: {
      label: 'Subsystem',
      cell: {
        kind: 'component',
        component: markRaw(BusModuleSubsystem),
        componentProps: data => ({ busModuleSubsystem: data.busModuleSubsystem }),
      },
    },
    minPaxNumber: {
      label: 'Min vehicle capacity',
      header: {
        sort: 'minPaxNumber',
      },
      cell: {
        kind: 'simple',
        path: 'minPaxNumber',
      },
    },
    driverSource: {
      label: 'Driver Source',
      cell: {
        kind: 'component',
        component: markRaw(ManualDriverSource),
        componentProps: sj => ({
          driverSource: sj.driverSource,
          useManualDriverSourceValues: sj.useManualDriverSourceValues,
          manualDefaultShiftLength: sj.manualDefaultShiftLength,
          manualMinTimeBetweenShifts: sj.manualMinTimeBetweenShifts,
          manualMinBreakTime: sj.manualMinBreakTime,
          manualMaxBreakTime: sj.manualMaxBreakTime,
          manualMaxContDrivingTime: sj.manualMaxContDrivingTime,
        }),
      },
    },
    vehicleType: {
      label: 'Vehicle Type',
      cell: {
        kind: 'component',
        component: markRaw(VehicleType),
        componentProps: data => ({ vehicleType: data.vehicleType }),
      },
    },
    vehicleSource: {
      label: 'Vehicle Source',
      cell: {
        kind: 'component',
        component: markRaw(VehicleSource),
        componentProps: data => ({ vehicleSource: data.vehicleSource }),
      },
    },
    clientGroup: {
      label: 'Client Group',
      cell: {
        kind: 'component',
        component: markRaw(ClientGroup),
        componentProps: data => ({ clientGroup: data.clientGroup }),
      },
    },
    operationalServiceLevel: {
      label: 'OSL',
      tooltip: 'Operational service level',
      header: {},
      cell: {
        kind: 'component',
        component: markRaw(Osl),
        componentProps: data => ({ osl: data.operationalServiceLevel }),
      },
    },
  },
}
